import React from 'react';
import BaseAutocomplete from '@material-ui/lab/Autocomplete';
import { useTranslation } from 'react-i18next';
import { useField, useFormikContext } from 'formik';
import { Grid, InputLabel, TextField } from '@material-ui/core';

import { TEXT_FIELD_COLOR, TEXT_FIELD_VARIANT } from '../../enum/formProps';

import './Autocomplete.scss';
import { OptionList } from '../../../../shared/model/commonModel';

interface AutocompleteProps {
  label: string;
  name: string;
  options: {
    value: string;
    key: string | number;
  }[];
  color?: TEXT_FIELD_COLOR;
  variant?: TEXT_FIELD_VARIANT;
  disabled?: boolean;
  readOnly?: boolean;
}

const Autocomplete: React.FC<AutocompleteProps> = ({ label, ...props }) => {
  const { t } = useTranslation();
  const [field, meta] = useField(props);
  const error = meta.error as unknown as OptionList;
  const isError = !!(error?.key && meta.touched);
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const options = [
    {
      value: '',
      key: '',
    },
    ...props.options,
  ];

  return (
    <Grid container spacing={0} className="Autocomplete-Container">
      <Grid item xs={4} md={3} lg={2} xl={1}>
        <InputLabel className="Autocomplete-InputLabel" error={isError} disabled={props.disabled}>
          {t(label)}
        </InputLabel>
      </Grid>
      <Grid item xs={8} md={9} lg={10} xl={11}>
        <BaseAutocomplete
          className="Autocomplete-BaseAutocomplete"
          getOptionLabel={(option) => (option ? option.value : '')}
          getOptionSelected={(option, current) => option.value === current.value}
          id={props.name}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={isError ? t(error?.key ?? '') : ''}
              error={isError}
              color={props.color}
              fullWidth
              InputLabelProps={{ ...params.InputLabelProps, shrink: true, disableAnimation: true }}
              InputProps={
                !props.readOnly
                  ? {
                      ...params.InputProps,
                      disableUnderline: true,
                    }
                  : {
                      disableUnderline: true,
                      readOnly: true,
                    }
              }
            />
          )}
          {...props}
          {...field}
          onChange={(e, value) => setFieldValue(props.name, value)}
          onBlur={() => setFieldTouched(props.name, true)}
          options={options}
          disabled={props.disabled}
        />
      </Grid>
    </Grid>
  );
};

export default Autocomplete;
